import { useEffect } from 'react';

import { useLocation, useParams } from '../../../contexts/RouterContext';
import { queryClient, useQuery } from '../../../ReactQuery';
import type { FullRevisionResponse } from '../../../../shared/types/routes/revisions';
import type { RevisionRouteParams } from '../../../components/projects/projectDashboardTypes';

import { getQueryKey as getFullQueryKey, getRevision } from './useRevision';
import { getMinimalQueryKey, getMinimalRevision } from './useRevisionMinimal';

const POLL_INTERVAL = 7000;
// Revision data doesn't change that frequently, and by keeping this high (1 min), we can save some queries and make the app feel snappier when navigating back to a revision.
const STALE_TIME = 60_000;

/**
 * Determine if the current pathname is the project summary page.
 * We can tell that by splitting on the revisionId part, and seeing if anything comes after it.
 *
 * @example
 * isOnSummaryPage('/projects/custom%2B1%2Ftest/refs/branch/master/1234567890') // true
 * isOnSummaryPage('/projects/custom%2B1%2Ftest/refs/branch/master/1234567890/issues') // false
 * isOnSummaryPage('/projects/custom%2B1%2Ftest/refs/branch/master/1234567890/browse/dependencies') // false
 */
function isOnSummaryPage(pathname: string, revisionId?: string): boolean {
  if (!revisionId) {
    return false;
  }
  const [, afterRevisionId] = pathname.split(revisionId);
  return !afterRevisionId;
}

/** Returns true if the error is an HTTP 4xx response (permanent client error). */
function isClientError(error: unknown): boolean {
  const status = (error as Response)?.status;
  return !!status && status >= 400 && status < 500;
}

/**
 * Fetches the current revision based on URL params, with automatic polling
 * when the revision is in an unsteady state.
 *
 * Like `useCurrentProject`, it reads URL params to determine the resource to
 * fetch. It additionally handles minimal vs. full endpoint selection, as well as
 * automatic polling when the revision is in an unsteady state.
 *
 * Only the project summary page needs the full revision data, so all
 * other routes under the RevisionContainer component will use the minimal endpoint.
 *
 * Polling behavior:
 * - Full endpoint: polls every 7s until server-computed `isSteady` is true
 * - Minimal endpoint: polls every 7s until `resolved`
 * - Once steady, polling stops automatically
 *
 * NOTE: The old Reflux RevisionStore passed `checkDependencyCache=true` and
 * sent a `fossa-skip-request-cache: 1` header. Backend investigation confirms
 * that neither is read by any server code — both are dead. We intentionally
 * do not carry them forward.
 */
export function useCurrentRevision() {
  const params = useParams<RevisionRouteParams>();
  const location = useLocation();

  const revisionLocator = `${params.project_id}$${params.revision_id}`;
  const isSummaryPage = isOnSummaryPage(location.pathname, params.revision_id);
  //  If there is a latest revision on the project, then this will always be set via the ProjectContainer.
  //  If for some reason this there is no revision, then we handle it being falsey by not fetching anything.
  const shouldFetch = !!params.revision_id;

  // Full revision — with polling when unsteady.
  const fullRevision = useQuery<FullRevisionResponse>({
    queryKey: getFullQueryKey(revisionLocator),
    queryFn: () => getRevision(revisionLocator),
    enabled: shouldFetch && isSummaryPage,
    staleTime: STALE_TIME,
    refetchInterval: (data, query) => {
      // Continue polling even before the first successful response so that
      // transient errors (network blips, brief 500s) can recover without
      // requiring a manual page refresh. Stop on permanent client errors.
      if (!data) {
        return isClientError(query.state.error) ? false : POLL_INTERVAL;
      }
      return data.isSteady ? false : POLL_INTERVAL;
    },
  });

  // Minimal revision — polls only until resolved
  const minimalRevision = useQuery({
    queryKey: getMinimalQueryKey(revisionLocator),
    queryFn: () => getMinimalRevision(revisionLocator),
    enabled: shouldFetch && !isSummaryPage,
    staleTime: STALE_TIME,
    refetchInterval: (data, query) => {
      // Same rationale as above: poll through transient errors, stop on permanent ones.
      if (!data) {
        return isClientError(query.state.error) ? false : POLL_INTERVAL;
      }
      return data.resolved ? false : POLL_INTERVAL;
    },
  });

  // Seed the minimal cache with full revision data so that navigating from the
  // summary page to a sub-page skips the redundant minimal fetch (within staleTime).
  useEffect(() => {
    if (fullRevision.data) {
      queryClient.setQueryData(
        getMinimalQueryKey(revisionLocator),
        fullRevision.data,
      );
    }
  }, [fullRevision.data, revisionLocator]);

  const activeQuery = isSummaryPage ? fullRevision : minimalRevision;

  return {
    ...activeQuery,
    isMinimal: !isSummaryPage,
    isFullRevisionLoaded: isSummaryPage && !!fullRevision.data,
  };
}
